import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GLSLUniformLocations, PropertyValue, Vec2 } from './parser/propTypes';
import { selectStyles } from './styles/selectStyles';
import Select from 'react-select';
import { ImageBuffer } from './ImageBuffer';

// The canvas element + webgl context.
// Canvas takes a glsl fragment shader and executes it on a <canvas> element.

// In order to be able to apply a fragment shader to each pixel, we need an object for the fragment shader to be applied to. 
// We achieve this by placing a quad filling the entire screen. The vertex shader can just copy positions without any transformation.
export const vertexShaderSrc = 
`#version 300 es
in vec4 a_position;
void main() {
  gl_Position = a_position;
}
`;

// Create a WebGL position buffer with a screen filling quad
export function quadBuffer(gl: WebGL2RenderingContext) {
  const buffer = gl.createBuffer();
  if(buffer == null) {
    alert("Failed to create WebGL position buffer.");
    return;
  }
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);

  const pos = [
    -1.0, 1.0,
    1.0, 1.0,
    -1.0, -1.0,
    1.0, -1.0
  ];

  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(pos), gl.STATIC_DRAW);
}

// Create a texture with the given size and a framebuffer with the texture attached
// Binding the returned framebuffer to a webgl context will result in renders targeting the texture
export function makeTargetTexture(size: [number, number], gl: WebGL2RenderingContext): [WebGLTexture | null, WebGLFramebuffer | null] {
    // enable EXT_color_buffer_float WebGL extension
  // we use this to render to a floating point texture
  if(gl.getExtension("EXT_color_buffer_float") === null) {
    alert("Your browser's WebGL doesn't support the EXT_color_buffer_float extension.");
    return [null, null];
  }
  // create texture
  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);

  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, size[0], size[1], 0, gl.RGBA, gl.FLOAT, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

  // create framebuffer holding texture
  const fb = gl.createFramebuffer();
  gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
  const ap = gl.COLOR_ATTACHMENT0;
  gl.framebufferTexture2D(gl.FRAMEBUFFER, ap, gl.TEXTURE_2D, texture, 0);

  // Make sure the browser can render to the framebuffer
  if(gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
    alert("Your browser's WebGL doesn't support rendering to a floating point RBGA texture.");
  }

  return [texture, fb];
}

interface CanvasProps {
  // glsl fragment shader
  fragShaderSrc: string;
  // names of uniforms whose locations should be extracted
  uniforms: string[];
  setUniformLocs: (locs: GLSLUniformLocations) => void;
  uniformLocs: GLSLUniformLocations;
  // webgl context
  gl: WebGL2RenderingContext | null;
  setGLContext: (gl: WebGL2RenderingContext) => void;
  setCanvas: (canvas: HTMLCanvasElement) => void;
  // shader props
  props: PropertyValue[][];
  // if animation is enabled
  doAnimate: boolean;
  // canvas event handlers
  onWheel: (event: React.WheelEvent, mousePos: Vec2, aspectRatio: number, dist: number) => void;
  onKeyDown: (event: React.KeyboardEvent, mousePos: Vec2, aspectRatio: number, dist: number) => void;
  onMouseDrag: (event: React.MouseEvent, mousePos: Vec2, aspectRatio: number, dist: number) => void;
}

export default function Canvas(props: CanvasProps) {
  // canvas element for webgl rendering to
  const canvasGl = useRef<HTMLCanvasElement>(null);
  // canvas element for displaying progressive rendering results
  const canvas2D = useRef<HTMLCanvasElement>(null);
  // canvas 2d drawing context
  const ctx2D = useRef<CanvasRenderingContext2D | null>(null);
  // texture that we render into
  const targetTexture = useRef<[WebGLTexture | null, WebGLFramebuffer | null] | null>(null);
  // webgl program (containing vertex shader + active fragment shader)
  const [program, setProgram] = useState<WebGLProgram | null>(null);
  // Canvas size (width, height)
  const [size, setSize] = useState<[number, number]>([0, 0]);
  // Whether to use progressive rendering (whether to render the whole image at once, or build it up from tiles over time)
  const [progressive, setProgressive] = useState<boolean>(true);
  // Number of tiles in each dimension
  const [tiling, setTiling] = useState<number>(2);
  // Progressive rendering buffer
  const buffer = useRef<ImageBuffer>(new ImageBuffer(size, tiling));
  // if the render loop is running
  const [running, setRunning] = useState(true);
  // if the shader should be recompiled automatically or need the user to press compile
  const [compileAuto, setCompileAuto] = useState(true);
  const [recompileNeeded, setRecompileNeeded] = useState(false);
  // current requestAnimationFrame id
  const requestAnimateRef = useRef<number | null>(null);
  // current mouse position on canvas
  const mousePos = useRef<[number, number]>([0.0, 0.0]);
  // if the mouse is pressed
  const mousePressed = useRef<boolean>(false);
  // div containing canvas (used to dynamically size canvas)
  const divContainer = useRef<HTMLDivElement | null>(null);

  const { gl, setGLContext, fragShaderSrc, uniforms, setUniformLocs, onKeyDown, onMouseDrag, onWheel, setCanvas, doAnimate } = props;
  const tileSize = useMemo(() => progressive ? [size[0] / tiling, size[1] / tiling] : size, [progressive, size, tiling]);

  // setup webgl context
  useEffect(() => {
    if(canvas2D.current === null || canvasGl.current === null) return;
    // get webgl context
    const glCtx = canvasGl.current.getContext("webgl2", { preserveDrawingBuffer: true });
    if (glCtx === null) {
      alert("Failed to initialize WebGL. Perhaps your browser doesn't support WebGL 2?");
      return;
    }
    // get 2d drawing context for canvas
    ctx2D.current = canvas2D.current.getContext("2d");
    if(progressive) {
      setCanvas(canvas2D.current);
    } else {
      setCanvas(canvasGl.current);
    }

    // set canvas to black
    glCtx.clearColor(0.0, 0.0, 0.0, 1.0);
    glCtx.clear(glCtx.COLOR_BUFFER_BIT);

    // compile vertex shader
    const vertexShader = glCtx.createShader(glCtx.VERTEX_SHADER);
    if(vertexShader === null) {
      alert("Failed to create vertex shader.");
      return;
    }
    glCtx.shaderSource(vertexShader, vertexShaderSrc);
    glCtx.compileShader(vertexShader);

    // make sure vertex shader compiled
    if(!glCtx.getShaderParameter(vertexShader, glCtx.COMPILE_STATUS)) {
      alert("Failed to compile vertex shader: " + glCtx.getShaderInfoLog(vertexShader));
      glCtx.deleteShader(vertexShader);
      return;
    }

    // create program + attach vertex shader
    const glProgram = glCtx.createProgram();
    if(glProgram === null) {
      alert("Failed to create WebGL program.");
      return;
    }
    glCtx.attachShader(glProgram, vertexShader);
    // setup geometry
    quadBuffer(glCtx);

    setProgram(glProgram);
    setGLContext(glCtx);
    console.log("WebGL context setup.");
  }, [canvas2D, setGLContext, setCanvas]);

  // Render a frame
  const render = useCallback(() => {
    if(gl === null || program === null) return;
    // set framebuffer if in progressive rendering
    if(progressive) {
      if(targetTexture.current === null) return;
      gl.bindFramebuffer(gl.FRAMEBUFFER, targetTexture.current[1]);
    } else {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }

    // Clear the screen
    gl.clearColor(0.0, 0.0, 0.0, 1.0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    if(progressive) {
      gl.viewport(0, 0, tileSize[0], tileSize[1]);
    } else {
      gl.viewport(0, 0, size[0], size[1]);
    }
    // set uniforms
    gl.uniform2fv(gl.getUniformLocation(program, "u_resolution"), size);
    gl.uniform1i(gl.getUniformLocation(program, "u_progressive_enable"), progressive ? 1 : 0);
    gl.uniform3fv(gl.getUniformLocation(program, "u_progressive"), [...buffer.current.curTile, tiling]);
    gl.uniform1f(gl.getUniformLocation(program, "u_random"), Math.random());
    gl.uniform1i(gl.getUniformLocation(program, "u_interactive"), 1);

    // draw data in position buffer
    const aPositionLoc = gl.getAttribLocation(program, "a_position");
    gl.vertexAttribPointer(aPositionLoc, 2, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(aPositionLoc);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    if(progressive) {
      // progressive rendering: add to buffer + display buffer
      gl.readPixels(0, 0, tileSize[0], tileSize[1], gl.RGBA, gl.FLOAT, buffer.current.tile);
      buffer.current.addTile();
      if(ctx2D.current !== null) {
        buffer.current.draw(ctx2D.current);
      }
    }

    requestAnimateRef.current = requestAnimationFrame(render);
  }, [gl, program, size, progressive, tileSize, tiling]);

  // start the render loop
  const startRun = useCallback(() => {
    if(requestAnimateRef.current !== null) {
      cancelAnimationFrame(requestAnimateRef.current);
    }
    setRunning(true);
    requestAnimateRef.current = requestAnimationFrame(render);
  }, [render]);

  // cancel the currently running render sequence
  const cancelRun = useCallback(() => {
    setRunning(false);
    if(requestAnimateRef.current !== null) {
      cancelAnimationFrame(requestAnimateRef.current);
    }
  }, []);

  // load fragment shader + compile
  const compileShader = useCallback(() => {
    // if webgl context hasn't been set up yet, don't do anything
    if(gl === null || program === null) return;
    // detach + delete old fragment shader if present
    const curShaders = gl.getAttachedShaders(program);
    if(curShaders !== null) {
      curShaders.forEach((shader) => {
        if(gl.getShaderParameter(shader, gl.SHADER_TYPE) === gl.FRAGMENT_SHADER) {
          gl.detachShader(program, shader);
        }
      });
    }

    // create + compile fragment shader
    const fragShader = gl.createShader(gl.FRAGMENT_SHADER);
    if(fragShader === null) {
      alert("Failed to create fragment shader.");
      return;
    }
    gl.shaderSource(fragShader, fragShaderSrc);
    gl.compileShader(fragShader);

    // make sure fragment shader compiled
    if(!gl.getShaderParameter(fragShader, gl.COMPILE_STATUS)) {
      // TODO: parse + nicely display fragment shader compile failure
      alert("Failed to compile fragment shader: " + gl.getShaderInfoLog(fragShader));
      return;
    }

    // attach fragment shader + link program
    gl.attachShader(program, fragShader);
    gl.linkProgram(program);

    // make sure program linked successfully
    if(!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      // TODO: parse + nicely display program link failure
      alert("Failed to link WebGL program: " + gl.getProgramInfoLog(program));
      return;
    }

    // extract uniform locations
    let uniformLocs: GLSLUniformLocations = {};
    uniforms.forEach((uniformName) => {
      const loc = gl.getUniformLocation(program, uniformName);
      uniformLocs[uniformName] = loc;
    });
    setUniformLocs(uniformLocs);

    gl.useProgram(program);

    console.log("WebGL shaders compiled.");
    setRecompileNeeded(false);
    if(running && requestAnimateRef.current === null) {
      startRun();
    }
  }, [gl, program, fragShaderSrc, uniforms, setUniformLocs, running, startRun]);

  // When shader dependencies change compile the shader
  useEffect(() => {
    if(compileAuto) {
      compileShader();
    } else {
      setRecompileNeeded(true);
    }
  }, [compileShader, compileAuto]);

  // Set canvas size to match available space
  // return true if size changed
  const resizeCanvas = useCallback(() => {
    if(divContainer.current === null || canvasGl.current === null || canvas2D.current === null) return;

    const currentSize = [canvas2D.current.width, canvas2D.current.height];

    // in order for divContainer to have the correct size, we need to temporarily set the canvas to 0 size
    // otherwise, divContainer's size will be affected by canvas size
    canvas2D.current.width = 0;
    canvas2D.current.height = 0;
    canvasGl.current.width = 0;
    canvasGl.current.height = 0;
    // get size of canvas containing div (-30px vertical to create room for controls)
    // round canvas sizes to nearest multiple of 8 (to allow upto 8x8 tiles)
    const divSize = divContainer.current.getBoundingClientRect();
    const newSize: [number, number] = [Math.floor(divSize.width / 8) * 8, Math.floor((divSize.height - 30) / 8) * 8];
    setSize(newSize);

    // set canvas sizes
    canvas2D.current.width = newSize[0];
    canvas2D.current.height = newSize[1];
    canvasGl.current.width = progressive ? newSize[0] / tiling : newSize[0];
    canvasGl.current.width = progressive ? newSize[1] / tiling : newSize[1];
    // check if size changed
    return (newSize[0] !== currentSize[0] || newSize[1] !== currentSize[1]) && (currentSize[0] !== 0 || currentSize[1] !== 0);
  }, [divContainer]);

  // resize at start + whenever screen size changes
  useEffect(() => {
    resizeCanvas();
    const callback = () => { resizeCanvas(); cancelRun(); };
    window.addEventListener('resize', callback);
    return () => {
      window.removeEventListener("resize", callback);
    };
  }, [resizeCanvas, cancelRun]);

  // resize when animation enabled/disabled state changes (presence of animation controls changes canvas size)
  useEffect(() => {
    if(resizeCanvas()) {
      cancelRun();
    }
  }, [doAnimate]);

  // Whenever the canvas is resized, recreate render target at appropriate size
  useEffect(() => {
    if(gl === null) return;

    if(targetTexture.current !== null) {
      gl.deleteTexture(targetTexture.current[0]);
      gl.deleteFramebuffer(targetTexture.current[1]);
    }

    targetTexture.current = makeTargetTexture([size[0] / tiling, size[1] / tiling], gl);
    buffer.current = new ImageBuffer(size, tiling);
  }, [gl, size, tiling]);

  // Clear image buffer if props are changed
  useEffect(() => {
    if(!progressive) return;

    buffer.current.clear();
  }, [props.props, progressive]);

  // Cancel run on unmount
  useEffect(() => {
    return () => {
      cancelRun();
    };
  }, []);

  // Get dynamic camera movement coefficient (pixel in top left corner of canvas)
  const cameraMoveCoeff = useCallback(() => {
    if(!progressive) return -1.0;

    const dist = buffer.current.tile[0] * 100.0;

    return dist !== Infinity && !isNaN(dist) ? dist : -1.0;
  }, [progressive]);

  // Callback to keep track of mouse position on canvas
  const onMouseMove = useCallback((event: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
    if(canvas2D.current === null || canvasGl.current === null) return;

    const canvasPos = progressive ? 
      canvas2D.current.getBoundingClientRect() :
      canvasGl.current.getBoundingClientRect();
    // store mouse pos in [0.0, 1.0] range
    mousePos.current = [(event.clientX - canvasPos.left) / size[0], (event.clientY - canvasPos.top) / size[1]];
    // If mouse is clicked, call mouse drag handler
    if(mousePressed.current) {
      const delta: Vec2 = [event.nativeEvent.movementX / size[0], event.nativeEvent.movementY / size[1]];
      onMouseDrag(event, delta, size[0] / size[1], cameraMoveCoeff());
    }
  }, [onMouseDrag, size, cameraMoveCoeff, progressive]);

  const canvasProps = {
    onMouseMove,
    onMouseDown: () => { mousePressed.current = true; },
    onMouseUp: () => { mousePressed.current = false; },
    onMouseLeave: () => { mousePressed.current = false; },
    onWheel: (event: React.WheelEvent) => onWheel(event, mousePos.current, size[0] / size[1], cameraMoveCoeff()),
    onKeyDown: (event: React.KeyboardEvent) => onKeyDown(event, mousePos.current, size[0] / size[1], cameraMoveCoeff()),
    tabIndex: 1
  };

  const tilingOptions = [1, 2, 4, 8].map((tiling) => ({ value: tiling, label: `${tiling * tiling} Tiles` }));

  return (
    <div className={`canvasDiv ${!doAnimate ? 'noAnimate' : ''}`} ref={divContainer}>
      {/* webgl target canvas 
        * directly rendered to in non-progressive mode, tile target in progressive */}
      <canvas
        {...canvasProps}
        width={tileSize[0]} height={tileSize[1]}
        ref={canvasGl}
        style={{display: progressive ? "none" : "block"}}
      ></canvas>
      
      <canvas
        {...canvasProps}
        width={size[0]} height={size[1]}
        ref={canvas2D}
        style={{display: progressive ? "block" : "none"}}
      ></canvas>

      <div className="canvasControls">
        <input type="checkbox" checked={compileAuto} onChange={(event) => setCompileAuto(event.target.checked)} />
        <span>Auto-Compile</span>
        {!compileAuto &&
          <button onClick={compileShader} className={`${recompileNeeded ? "needCompile" : ""}`}>Compile Shader</button>
        }
        {running &&
          <button onClick={cancelRun}>Stop Running</button>
        }
        {!running &&
          <>
            <button onClick={startRun}>Start Running</button>
            <div className="centerRunButton">
              <button onClick={startRun}>Run Fractal</button>
            </div>
          </>
        }
        <input 
          type="checkbox" checked={progressive} 
          onChange={(event) => {
            setProgressive(event.target.checked);
            if(event.target.checked) {
              setCanvas(canvas2D.current!);
            } else {
              setCanvas(canvasGl.current!);
            }
            cancelRun();
          }} 
        />
        <span>Progressive Rendering</span>
        {progressive && 
          <Select 
            options={tilingOptions}
            value={{value: tiling, label: `${tiling * tiling} Tiles`}}
            onChange={(option) => {
              if(option !== null) {
                setTiling(option.value);
                cancelRun();
              }
            }}
            styles={selectStyles}
            className="selectComponent inlineSelect"
            menuPlacement="top"
          />
        }
      </div>
    </div>
  );
}
